/**
 * Promotion Management - Unified interface for promotion planning, scenario comparison,
 * lift estimation, and post-promotion analysis.
 */
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  SelectChangeEvent,
} from '@mui/material';
import {
  CalendarMonth,
  Compare,
  TrendingUp,
  Assessment,
  Add,
  Visibility,
  CheckCircle,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import api from '../../services/api';
import TabPanel from '../Common/TabPanel';
import { useCurrencyFormat } from '../../hooks/useCurrencyFormat';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PromoEvent {
  event_id: string;
  event_name: string;
  start_date: string;
  end_date: string;
  discount: string;
  status: 'planned' | 'confirmed' | 'in_progress' | 'completed';
  selected_scenario_id?: string;
  selected_scenario_name?: string;
  details?: Record<string, any>;
}

interface PromoScenario {
  scenario_id: string;
  event_id: string;
  scenario_name: string;
  discount_type: string;
  discount_value: number;
  predicted_lift_pct: number;
  predicted_revenue: number;
  predicted_margin: number;
  predicted_roi_pct: number;
  stock_required: number;
  supplier_discount_pct: number;
  supplier_compensation_mad: number;
  is_selected: boolean;
  promo_price?: number;
}

interface LiftEstimationResult {
  predicted_lift_pct: number;
  confidence_level: string;
  based_on_count: number;
  historical_lifts?: Array<{ label: string; lift_pct: number }>;
}

interface PostAnalysisRow {
  metric: string;
  predicted: number;
  actual: number;
  variance: number;
}

// ---------------------------------------------------------------------------
// Status chip color mapping
// ---------------------------------------------------------------------------

const statusColorMap: Record<string, 'info' | 'warning' | 'success' | 'default'> = {
  planned: 'info',
  confirmed: 'warning',
  in_progress: 'success',
  completed: 'default',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (value: number | undefined | null): string => {
  if (value == null) return '-';
  return new Intl.NumberFormat('fr-MA', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
};

const today = (): string => new Date().toISOString().split('T')[0];

const threeMonthsAgo = (): string => {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().split('T')[0];
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const PromotionManagement: React.FC = () => {
  const { t } = useTranslation();
  const { fmtCurrency, fmtCurrencyCompact, fmtCurrencyPrecise, fmtCurrencyLabel } = useCurrencyFormat();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);

  // ---- Tab 1 state ----
  const [dateFrom, setDateFrom] = useState(threeMonthsAgo());
  const [dateTo, setDateTo] = useState(today());
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailEvent, setDetailEvent] = useState<PromoEvent | null>(null);

  // ---- Tab 2 state ----
  const [selectedEventId, setSelectedEventId] = useState('');
  const [addScenarioOpen, setAddScenarioOpen] = useState(false);
  const [newScenario, setNewScenario] = useState({
    scenario_name: '',
    discount_type: 'percentage',
    discount_value: 0,
    supplier_discount_pct: 0,
    supplier_compensation_mad: 0,
    promo_price: 0,
  });

  // ---- Tab 3 state ----
  const [liftForm, setLiftForm] = useState({
    product_or_category: '',
    discount_type: 'percentage',
    discount_value: 0,
  });

  // ---- Tab 4 state ----
  const [postEventId, setPostEventId] = useState('');
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [recordForm, setRecordForm] = useState({
    actual_lift_pct: 0,
    actual_additional_units: 0,
    actual_revenue: 0,
    actual_margin: 0,
    actual_roi_pct: 0,
  });

  // ===========================================================================
  // QUERIES
  // ===========================================================================

  // Tab 1: Promotion calendar
  const {
    data: calendarData,
    isLoading: calendarLoading,
    error: calendarError,
  } = useQuery<PromoEvent[]>({
    queryKey: ['promoCalendar', dateFrom, dateTo],
    queryFn: async () => {
      const res = await api.getPromoCalendar({ date_from: dateFrom, date_to: dateTo });
      return res.data?.events ?? res.data ?? [];
    },
    enabled: !!dateFrom && !!dateTo,
  });

  // Tab 2: Scenarios for selected event
  const {
    data: scenariosData,
    isLoading: scenariosLoading,
  } = useQuery<PromoScenario[]>({
    queryKey: ['promoScenarios', selectedEventId],
    queryFn: async () => {
      const res = await api.getPromoScenarios(selectedEventId);
      return res.data?.scenarios ?? res.data ?? [];
    },
    enabled: !!selectedEventId,
  });

  // Tab 3: Lift estimation result (triggered via mutation)
  const liftMutation = useMutation<LiftEstimationResult, Error, typeof liftForm>({
    mutationFn: async (form) => {
      const res = await api.estimatePromoLift({
        product_or_category: form.product_or_category,
        discount_type: form.discount_type,
        discount_value: form.discount_value,
      });
      return res.data;
    },
  });

  // Tab 4: Post-promotion analysis
  const {
    data: postAnalysisData,
    isLoading: postAnalysisLoading,
  } = useQuery<PostAnalysisRow[]>({
    queryKey: ['promoPostAnalysis', postEventId],
    queryFn: async () => {
      const res = await api.comparePromoScenarios(postEventId);
      const raw = res.data;
      // The API may return a comparison object; normalise to rows
      if (Array.isArray(raw)) return raw;
      if (raw?.rows) return raw.rows;
      // Build rows from predicted vs actual keys
      const metrics = ['Lift %', 'Additional Units', 'Revenue', 'Margin', 'ROI'];
      const keys = ['lift_pct', 'additional_units', 'revenue', 'margin', 'roi_pct'];
      return metrics.map((metric, i) => ({
        metric,
        predicted: raw?.predicted?.[keys[i]] ?? 0,
        actual: raw?.actual?.[keys[i]] ?? 0,
        variance: raw?.variance?.[keys[i]] ?? 0,
      }));
    },
    enabled: !!postEventId,
  });

  // ===========================================================================
  // MUTATIONS
  // ===========================================================================

  const selectScenarioMutation = useMutation({
    mutationFn: (scenarioId: string) => api.selectPromoScenario(scenarioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promoScenarios', selectedEventId] });
      queryClient.invalidateQueries({ queryKey: ['promoCalendar'] });
    },
  });

  const createScenarioMutation = useMutation({
    mutationFn: (data: typeof newScenario) =>
      api.createPromoScenario(selectedEventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promoScenarios', selectedEventId] });
      setAddScenarioOpen(false);
      setNewScenario({
        scenario_name: '',
        discount_type: 'percentage',
        discount_value: 0,
        supplier_discount_pct: 0,
        supplier_compensation_mad: 0,
        promo_price: 0,
      });
    },
  });

  const recordResultsMutation = useMutation({
    mutationFn: (data: typeof recordForm) =>
      api.recordPromoResults(postEventId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promoPostAnalysis', postEventId] });
      setRecordDialogOpen(false);
    },
  });

  // ===========================================================================
  // Derived data
  // ===========================================================================

  const completedEvents = useMemo(
    () => (calendarData ?? []).filter((e) => e.status === 'completed'),
    [calendarData],
  );

  // ===========================================================================
  // TAB 1 - Promotion Calendar
  // ===========================================================================

  const renderCalendar = () => (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label={t('promotions.dateFrom')}
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextField
            label={t('promotions.dateTo')}
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            size="small"
          />
        </Grid>
      </Grid>

      {calendarLoading && <CircularProgress />}
      {calendarError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('promotions.errorLoadingCalendar')}
        </Alert>
      )}

      {!calendarLoading && calendarData && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('promotions.eventName')}</TableCell>
                <TableCell>{t('promotions.start')}</TableCell>
                <TableCell>{t('promotions.end')}</TableCell>
                <TableCell>{t('promotions.discount')}</TableCell>
                <TableCell>{t('promotions.status')}</TableCell>
                <TableCell>{t('promotions.selectedScenario')}</TableCell>
                <TableCell align="center">{t('promotions.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {calendarData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    {t('promotions.noEvents')}
                  </TableCell>
                </TableRow>
              ) : (
                calendarData.map((event) => (
                  <TableRow key={event.event_id}>
                    <TableCell>{event.event_name}</TableCell>
                    <TableCell>{event.start_date}</TableCell>
                    <TableCell>{event.end_date}</TableCell>
                    <TableCell>{event.discount}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(`promotions.status_${event.status}`)}
                        color={statusColorMap[event.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{event.selected_scenario_name ?? '-'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('promotions.viewDetails')}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setDetailEvent(event);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{detailEvent?.event_name}</DialogTitle>
        <DialogContent dividers>
          {detailEvent && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography>
                <strong>{t('promotions.start')}:</strong> {detailEvent.start_date}
              </Typography>
              <Typography>
                <strong>{t('promotions.end')}:</strong> {detailEvent.end_date}
              </Typography>
              <Typography>
                <strong>{t('promotions.discount')}:</strong> {detailEvent.discount}
              </Typography>
              <Typography>
                <strong>{t('promotions.status')}:</strong>{' '}
                <Chip
                  label={t(`promotions.status_${detailEvent.status}`)}
                  color={statusColorMap[detailEvent.status] ?? 'default'}
                  size="small"
                />
              </Typography>
              <Typography>
                <strong>{t('promotions.selectedScenario')}:</strong>{' '}
                {detailEvent.selected_scenario_name ?? '-'}
              </Typography>
              {detailEvent.details &&
                Object.entries(detailEvent.details).map(([key, val]) => (
                  <Typography key={key}>
                    <strong>{key}:</strong> {String(val)}
                  </Typography>
                ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            {t('promotions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ===========================================================================
  // TAB 2 - Scenario Comparison
  // ===========================================================================

  const renderScenarios = () => (
    <Box>
      {/* Event selector */}
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('promotions.selectEvent')}</InputLabel>
            <Select
              value={selectedEventId}
              label={t('promotions.selectEvent')}
              onChange={(e: SelectChangeEvent) => setSelectedEventId(e.target.value)}
            >
              {(calendarData ?? []).map((ev) => (
                <MenuItem key={ev.event_id} value={ev.event_id}>
                  {ev.event_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={!selectedEventId}
            onClick={() => setAddScenarioOpen(true)}
          >
            {t('promotions.addScenario')}
          </Button>
        </Grid>
      </Grid>

      {scenariosLoading && <CircularProgress />}

      {!scenariosLoading && scenariosData && (
        <Grid container spacing={2}>
          {scenariosData.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">{t('promotions.noScenarios')}</Alert>
            </Grid>
          ) : (
            scenariosData.map((sc) => (
              <Grid item xs={12} sm={6} md={4} key={sc.scenario_id}>
                <Card
                  variant="outlined"
                  sx={{
                    borderColor: sc.is_selected ? 'primary.main' : 'divider',
                    borderWidth: sc.is_selected ? 2 : 1,
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {sc.scenario_name}
                      {sc.is_selected && (
                        <CheckCircle
                          color="primary"
                          fontSize="small"
                          sx={{ ml: 1, verticalAlign: 'middle' }}
                        />
                      )}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {t('promotions.discountType')}: {sc.discount_type} &mdash;{' '}
                      {t('promotions.value')}: {fmt(sc.discount_value)}
                    </Typography>

                    <Grid container spacing={1} sx={{ mt: 1 }}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('promotions.predictedLift')}
                        </Typography>
                        <Typography variant="h6">{fmt(sc.predicted_lift_pct)}%</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('promotions.predictedRevenue')}
                        </Typography>
                        <Typography variant="h6">
                          {fmtCurrency(sc.predicted_revenue)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('promotions.predictedMargin')}
                        </Typography>
                        <Typography variant="body1">
                          {fmtCurrency(sc.predicted_margin)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('promotions.predictedROI')}
                        </Typography>
                        <Typography variant="body1">{fmt(sc.predicted_roi_pct)}%</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('promotions.stockRequired')}
                        </Typography>
                        <Typography variant="body1">{fmt(sc.stock_required)}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('promotions.supplierDiscount')}
                        </Typography>
                        <Typography variant="body1">
                          {fmt(sc.supplier_discount_pct)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          {t('promotions.supplierCompensation')}
                        </Typography>
                        <Typography variant="body1">
                          {fmtCurrency(sc.supplier_compensation_mad)}
                        </Typography>
                      </Grid>
                    </Grid>

                    {!sc.is_selected && (
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        sx={{ mt: 2 }}
                        disabled={selectScenarioMutation.isPending}
                        onClick={() => selectScenarioMutation.mutate(sc.scenario_id)}
                      >
                        {t('promotions.selectThisScenario')}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      )}

      {/* Add Scenario Dialog */}
      <Dialog
        open={addScenarioOpen}
        onClose={() => setAddScenarioOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('promotions.addScenario')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('promotions.scenarioName')}
              value={newScenario.scenario_name}
              onChange={(e) =>
                setNewScenario((prev) => ({ ...prev, scenario_name: e.target.value }))
              }
              fullWidth
              size="small"
            />
            <FormControl fullWidth size="small">
              <InputLabel>{t('promotions.discountType')}</InputLabel>
              <Select
                value={newScenario.discount_type}
                label={t('promotions.discountType')}
                onChange={(e: SelectChangeEvent) =>
                  setNewScenario((prev) => ({ ...prev, discount_type: e.target.value }))
                }
              >
                <MenuItem value="percentage">{t('promotions.discountType_percentage')}</MenuItem>
                <MenuItem value="fixed_amount">{t('promotions.discountType_fixedAmount')}</MenuItem>
                <MenuItem value="bogo">{t('promotions.discountType_bogo')}</MenuItem>
                <MenuItem value="gwp">{t('promotions.discountType_gwp')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('promotions.discountValue')}
              type="number"
              value={newScenario.discount_value}
              onChange={(e) =>
                setNewScenario((prev) => ({
                  ...prev,
                  discount_value: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label={t('promotions.supplierDiscountPct')}
              type="number"
              value={newScenario.supplier_discount_pct}
              onChange={(e) =>
                setNewScenario((prev) => ({
                  ...prev,
                  supplier_discount_pct: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label={t('promotions.supplierCompensationMad')}
              type="number"
              value={newScenario.supplier_compensation_mad}
              onChange={(e) =>
                setNewScenario((prev) => ({
                  ...prev,
                  supplier_compensation_mad: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label={t('promotions.promoPrice')}
              type="number"
              value={newScenario.promo_price}
              onChange={(e) =>
                setNewScenario((prev) => ({
                  ...prev,
                  promo_price: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddScenarioOpen(false)}>
            {t('promotions.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={!newScenario.scenario_name || createScenarioMutation.isPending}
            onClick={() => createScenarioMutation.mutate(newScenario)}
          >
            {createScenarioMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('promotions.create')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ===========================================================================
  // TAB 3 - Lift Estimation
  // ===========================================================================

  const renderLiftEstimation = () => (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="flex-end">
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('promotions.productOrCategory')}
            value={liftForm.product_or_category}
            onChange={(e) =>
              setLiftForm((prev) => ({ ...prev, product_or_category: e.target.value }))
            }
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('promotions.discountType')}</InputLabel>
            <Select
              value={liftForm.discount_type}
              label={t('promotions.discountType')}
              onChange={(e: SelectChangeEvent) =>
                setLiftForm((prev) => ({ ...prev, discount_type: e.target.value }))
              }
            >
              <MenuItem value="percentage">{t('promotions.discountType_percentage')}</MenuItem>
              <MenuItem value="fixed_amount">{t('promotions.discountType_fixedAmount')}</MenuItem>
              <MenuItem value="bogo">{t('promotions.discountType_bogo')}</MenuItem>
              <MenuItem value="gwp">{t('promotions.discountType_gwp')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField
            label={t('promotions.discountValue')}
            type="number"
            value={liftForm.discount_value}
            onChange={(e) =>
              setLiftForm((prev) => ({
                ...prev,
                discount_value: parseFloat(e.target.value) || 0,
              }))
            }
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <Button
            variant="contained"
            startIcon={<TrendingUp />}
            fullWidth
            disabled={!liftForm.product_or_category || liftMutation.isPending}
            onClick={() => liftMutation.mutate(liftForm)}
          >
            {liftMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('promotions.estimateLift')
            )}
          </Button>
        </Grid>
      </Grid>

      {liftMutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('promotions.errorEstimatingLift')}
        </Alert>
      )}

      {liftMutation.isSuccess && liftMutation.data && (
        <Box>
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'var(--color-info-bg)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('promotions.predictedLift')}
                  </Typography>
                  <Typography variant="h3" color="primary.main">
                    {fmt(liftMutation.data.predicted_lift_pct)}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'var(--color-success-bg)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('promotions.confidenceLevel')}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip
                      label={liftMutation.data.confidence_level}
                      color={
                        liftMutation.data.confidence_level === 'high'
                          ? 'success'
                          : liftMutation.data.confidence_level === 'medium'
                            ? 'warning'
                            : 'default'
                      }
                    />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Card sx={{ bgcolor: 'var(--color-warning-bg)' }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('promotions.basedOnHistorical')}
                  </Typography>
                  <Typography variant="h3">
                    {liftMutation.data.based_on_count}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Historical lift chart */}
          {liftMutation.data.historical_lifts &&
            liftMutation.data.historical_lifts.length > 0 && (
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  {t('promotions.historicalLifts')}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={liftMutation.data.historical_lifts}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis unit="%" />
                    <RechartsTooltip
                      formatter={(value: number) => [`${fmt(value)}%`, t('promotions.lift')]}
                    />
                    <Legend />
                    <Bar
                      dataKey="lift_pct"
                      name={t('promotions.lift')}
                      fill="#8B2438"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            )}
        </Box>
      )}
    </Box>
  );

  // ===========================================================================
  // TAB 4 - Post-Promotion Analysis
  // ===========================================================================

  const renderPostAnalysis = () => (
    <Box>
      <Grid container spacing={2} sx={{ mb: 3 }} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel>{t('promotions.selectCompletedEvent')}</InputLabel>
            <Select
              value={postEventId}
              label={t('promotions.selectCompletedEvent')}
              onChange={(e: SelectChangeEvent) => setPostEventId(e.target.value)}
            >
              {completedEvents.map((ev) => (
                <MenuItem key={ev.event_id} value={ev.event_id}>
                  {ev.event_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item>
          <Button
            variant="outlined"
            disabled={!postEventId}
            onClick={() => setRecordDialogOpen(true)}
          >
            {t('promotions.recordResults')}
          </Button>
        </Grid>
      </Grid>

      {postAnalysisLoading && <CircularProgress />}

      {!postAnalysisLoading && postAnalysisData && postAnalysisData.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('promotions.metric')}</TableCell>
                <TableCell align="right">{t('promotions.predicted')}</TableCell>
                <TableCell align="right">{t('promotions.actual')}</TableCell>
                <TableCell align="right">{t('promotions.variance')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {postAnalysisData.map((row) => (
                <TableRow key={row.metric}>
                  <TableCell>{row.metric}</TableCell>
                  <TableCell align="right">{fmt(row.predicted)}</TableCell>
                  <TableCell align="right">{fmt(row.actual)}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: row.variance >= 0 ? 'success.main' : 'error.main',
                      fontWeight: 600,
                    }}
                  >
                    {row.variance >= 0 ? '+' : ''}
                    {fmt(row.variance)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!postAnalysisLoading && postEventId && (!postAnalysisData || postAnalysisData.length === 0) && (
        <Alert severity="info">{t('promotions.noPostAnalysisData')}</Alert>
      )}

      {/* Record Results Dialog */}
      <Dialog
        open={recordDialogOpen}
        onClose={() => setRecordDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('promotions.recordResults')}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label={t('promotions.actualLiftPct')}
              type="number"
              value={recordForm.actual_lift_pct}
              onChange={(e) =>
                setRecordForm((prev) => ({
                  ...prev,
                  actual_lift_pct: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label={t('promotions.actualAdditionalUnits')}
              type="number"
              value={recordForm.actual_additional_units}
              onChange={(e) =>
                setRecordForm((prev) => ({
                  ...prev,
                  actual_additional_units: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label={t('promotions.actualRevenue') + ' ' + fmtCurrencyLabel()}
              type="number"
              value={recordForm.actual_revenue}
              onChange={(e) =>
                setRecordForm((prev) => ({
                  ...prev,
                  actual_revenue: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label={t('promotions.actualMargin') + ' ' + fmtCurrencyLabel()}
              type="number"
              value={recordForm.actual_margin}
              onChange={(e) =>
                setRecordForm((prev) => ({
                  ...prev,
                  actual_margin: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
            <TextField
              label={t('promotions.actualROI') + ' (%)'}
              type="number"
              value={recordForm.actual_roi_pct}
              onChange={(e) =>
                setRecordForm((prev) => ({
                  ...prev,
                  actual_roi_pct: parseFloat(e.target.value) || 0,
                }))
              }
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRecordDialogOpen(false)}>
            {t('promotions.cancel')}
          </Button>
          <Button
            variant="contained"
            disabled={recordResultsMutation.isPending}
            onClick={() => recordResultsMutation.mutate(recordForm)}
          >
            {recordResultsMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              t('promotions.save')
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  // ===========================================================================
  // RENDER
  // ===========================================================================

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('promotions.title')}
      </Typography>

      <Card>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab
            icon={<CalendarMonth />}
            label={t('promotions.calendar')}
            iconPosition="start"
          />
          <Tab
            icon={<Compare />}
            label={t('promotions.scenarios')}
            iconPosition="start"
          />
          <Tab
            icon={<TrendingUp />}
            label={t('promotions.liftEstimation')}
            iconPosition="start"
          />
          <Tab
            icon={<Assessment />}
            label={t('promotions.postAnalysis')}
            iconPosition="start"
          />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TabPanel value={tab} index={0}>
            {renderCalendar()}
          </TabPanel>
          <TabPanel value={tab} index={1}>
            {renderScenarios()}
          </TabPanel>
          <TabPanel value={tab} index={2}>
            {renderLiftEstimation()}
          </TabPanel>
          <TabPanel value={tab} index={3}>
            {renderPostAnalysis()}
          </TabPanel>
        </Box>
      </Card>
    </Box>
  );
};

export default PromotionManagement;
